import type { ThemeId, Tradition } from '../theme/tokens';

export type Gender = 'male' | 'female';

/** Billing/entitlement is a separate integration not built yet -- every user is "free" until
 * then. Never settable via PUT /api/me (see docs/PRACTICE_LEVELS.md's "Subscription model"). */
export type Tier = 'free' | 'premium';

/** A single, global, uncapped title derived from lifetime completions across every pillar and
 * tradition -- there is no "max rank" shown, ever, since the underlying count has no ceiling. */
export type PilgrimRank = 'Seeker' | 'Practitioner' | 'Adherent' | 'Adept' | 'Elder' | 'Sage' | 'Luminary';

export interface Me {
  sub: string;
  email: string;
  displayName: string;
  traditions: Tradition[];
  themePreference: ThemeId;
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string | null;
  leaderboardOptIn: boolean;
  /** Which standing mascot illustration + greeting voice to show -- cosmetic only. */
  gender: Gender | null;
  /** The one "main practice" among traditions[] to feature first on Home. */
  primaryTradition: Tradition | null;
  /** Every practiceId ever completed -- powers the Practices page's level-by-level unlock. */
  completedPracticeIds: string[];
  tier: Tier;
  /** Streak Shields (capped at 2, identical on both tiers) -- a missed day silently consumes one
   * instead of resetting the streak. Earned via POST /api/reinforcements, never bought. */
  shieldsAvailable: number;
  /** Kept for backward compatibility -- primary UI should prefer practiceMilestone (see the
   * Omnist Development Plan's "Progress and motivation": a spiritual-authority-sounding title
   * was replaced with concrete practice milestones in primary UI). */
  pilgrimRank: PilgrimRank;
  /** A validated IANA timezone name (e.g. "America/New_York"), or null if never set -- used to
   * compute the caller's *local* date for streak/reinforcement purposes. Unset means UTC. */
  timezone: string | null;
  /** Lifetime count of distinct local dates a session was completed on. Independent of
   * currentStreak (which resets on a gap) -- this only ever grows. */
  totalPracticeDays: number;
  /** The largest totalPracticeDays milestone already reached, or 0. */
  practiceMilestone: PracticeMilestone;
  /** Onboarding preferences (Work Package 2) -- optional/nullable, editable any time via
   * PUT /api/me, not onboarding-only. Feed GET /api/today's recommendation. */
  intent: OnboardingIntent | null;
  experienceLevel: ExperienceLevel | null;
  timeBudgetMinutes: number | null;
  /** Review fix F4: stamped once, server-side, when onboarding finishes (PUT /api/me
   * `completeOnboarding: true`). Null for legacy accounts that are complete only by inference. */
  onboardingCompletedAt: string | null;
  /** The route guard's source of truth -- independent of `traditions`, where an empty list means
   * "explore all traditions" (a legitimate finished choice, not an unfinished onboarding). */
  onboardingComplete: boolean;
  /** UI + content language. Drives which translated copy content routes return and which
   * string bundle the app renders chrome in (see docs/LOCALIZATION.md). */
  locale: Locale;
  /** Derived server-side from an env-var email allowlist (never stored, never client-settable)
   * -- true only for the handful of accounts with admin access to /admin. */
  isAdmin: boolean;
}

export type Locale = 'en' | 'es' | 'hi' | 'zh';

export interface AdminUser {
  sub: string;
  email: string | null;
  displayName: string | null;
  tier: Tier;
  /** Entitlement sources behind `tier` (code-review fix F3) -- optional so older responses parse. */
  paidPremium?: boolean;
  adminPremiumGrant?: boolean;
  traditions: Tradition[];
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string | null;
  totalPracticeDays: number;
  leaderboardOptIn: boolean;
  createdAt: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
}

export interface AdminAnalytics {
  totalUsers: number;
  premiumUsers: number;
  freeUsers: number;
  premiumRate: number;
  usersWithActiveStreak: number;
  avgCurrentStreak: number;
  avgLongestStreak: number;
  totalDistinctPracticeCompletions: number;
  leaderboardOptInRate: number;
  usersByTradition: Record<string, number>;
  usersWithSignupDate: number;
}

export type PracticeMilestone = 0 | 7 | 30 | 100 | 365;
export type OnboardingIntent = 'deepen' | 'explore' | 'routine';
export type ExperienceLevel = 'new' | 'some' | 'experienced';

export interface UpdateMeBody {
  displayName?: string;
  traditions?: Tradition[];
  themePreference?: ThemeId;
  leaderboardOptIn?: boolean;
  gender?: Gender | null;
  primaryTradition?: Tradition | null;
  timezone?: string | null;
  intent?: OnboardingIntent | null;
  experienceLevel?: ExperienceLevel | null;
  timeBudgetMinutes?: number | null;
  /** Ask the server to stamp onboardingCompletedAt now (review fix F4). Only `true` is accepted. */
  completeOnboarding?: true;
  locale?: Locale;
}

/** GET /api/today's response -- one deterministic, explainable recommendation. `practiceId` is
 * null only when the caller has no traditions selected or none have any content yet. */
export interface TodayPlan {
  practiceId: string | null;
  /** `standalone` (review fix 2026-09-10): a practice from a tradition with no step-by-step
   * pillar path yet -- recommended instead of wrongly reporting "nothing available". */
  reason: 'next_in_path' | 'standalone' | 'premium_preview' | 'review' | 'none';
  explanation: string;
  nextStep: string;
  /** Authorized summary of `practiceId` (null exactly when practiceId is null) -- enough to
   * render Today's card without fetching the catalog. Optional only for older servers. */
  practice?: TodayPracticeSummary | null;
}

/** GET /api/schedule's response -- a fixed sequencing of one pillar's already-authored levels
 * across the Development Plan's 30-day flagship structure. Never fabricated content: `type` is
 * "new" only for a level that actually exists; everything past the real ladder is honestly
 * "review"/"application"/"consolidation". */
export interface ThirtyDaySchedule {
  pillarId: string;
  days: ScheduleDay[];
}

export interface ScheduleDay {
  day: number;
  type: 'new' | 'review' | 'application' | 'consolidation';
  practiceId: string | null;
  note: string;
}

/** GET /api/public/sample (review fix F11) -- the one practice deliberately available to signed-out
 * visitors (only rows flagged `publicSample: true`, published, level 1, free). `reviewerStatus` is
 * passed through honestly ("draft" unless a real review is recorded). `contentLanguage` is the
 * language the transcript actually resolved to; `languageFallback` is true when any requested
 * translation was missing. No narration audio is offered to signed-out visitors. */
export interface PublicSample {
  practiceId: string;
  title: string;
  description: string;
  type: PracticeType;
  tradition: Tradition;
  pillarId: string | null;
  level: number | null;
  durationSeconds: number;
  transcript: string | null;
  requestedLanguage: Locale;
  contentLanguage: Locale;
  languageFallback: boolean;
  contentVersion: number;
  publicationState: 'draft' | 'published' | 'unpublished';
  reviewerStatus: 'draft' | 'reviewed';
  reviewerName: string | null;
  sourceRefs: string[];
  guidanceRequired: boolean;
}

export interface PublicSampleResponse {
  sample: PublicSample;
}

// --- Personal routine (Work Package 3) -----------------------------------------------------

export type DailyMoment = 'morning' | 'midday' | 'evening' | 'night';

export interface RoutineItem {
  practiceId: string;
  /** null only when the practice no longer exists (content removed) -- see `access`. */
  title: string | null;
  durationSeconds: number | null;
  access: PracticeAccess;
}

export interface Routine {
  items: RoutineItem[];
  dailyMoment: DailyMoment | null;
  totalDurationSeconds: number;
}

export interface UpdateRoutineBody {
  practiceIds?: string[];
  dailyMoment?: DailyMoment | null;
}

// --- Private journal (Work Package 3) ------------------------------------------------------

export interface JournalEntry {
  entryId: string;
  createdAt: string;
  updatedAt: string;
  text: string;
  practiceId: string | null;
}

export interface JournalEntriesResponse {
  entries: JournalEntry[];
  /** Opaque; pass back as `cursor` for the next page / export chunk. null (or absent, from an
   * older server) means there's nothing more. */
  nextCursor?: string | null;
}

export interface CreateJournalEntryBody {
  text: string;
  practiceId?: string | null;
}

export interface UpdateJournalEntryBody {
  text?: string;
  practiceId?: string | null;
}

export interface Quote {
  quoteId: string;
  text: string;
  author: string;
  tradition: Tradition;
}

export interface TodayQuotes {
  date: string;
  quotes: Quote[];
}

// The "skill types" every tradition's path is built from -- deliberately generic (not
// tradition-specific vocabulary) so the same four categories scale across all 17 traditions:
// - reading: a sacred/foundational text presented slowly, with reflection
// - prayer: a guided prayer/devotional, spoken by the mascot
// - meditation: silent or guided stillness practice
// - service: a real-world reflection or action prompt (the one skill that isn't audio-first)
// - movement: embodied instruction (e.g. qi gong/tai chi forms) -- taxonomy only for now, no
//   seed content is tagged with it yet (Work Package 2's "add movement as an explicit activity
//   type... instead of forcing it into stillness").
export type PracticeType = 'reading' | 'prayer' | 'meditation' | 'service' | 'movement';

export const PRACTICE_TYPE_LABELS: Record<PracticeType, string> = {
  reading: 'Reading',
  prayer: 'Prayer',
  meditation: 'Meditation',
  service: 'Service',
  movement: 'Movement',
};

export const PRACTICE_TYPE_ICONS: Record<PracticeType, string> = {
  reading: '📖',
  prayer: '🙏',
  meditation: '🧘',
  service: '🤝',
  movement: '🕺',
};

export interface Practice {
  practiceId: string;
  title: string;
  type: PracticeType;
  tradition: Tradition;
  /** Which Pillar this level belongs to -- null/undefined for a tradition not yet migrated onto
   * the pillar model, which falls back to the legacy flat duration-ordered path client-side. */
  pillarId?: string | null;
  /** Position within pillarId's ladder, 1-indexed. Absent for legacy (non-pillar) practices. */
  level?: number | null;
  durationSeconds: number;
  description: string;
  /** The full narration script, for an optional in-player transcript (Work Package 4). Protected
   * the same way the audio itself is: null unless `access === 'available'`, so a locked level's
   * script can never be read as a paywall bypass via the list endpoint. */
  transcript: string | null;
  /** Server-computed: true when this level is beyond the free tier's cap and the caller isn't
   * Premium. Kept for backward compatibility -- exactly `access === 'premium_required'`; prefer
   * `access` in new code, since it also distinguishes a progression lock from a paywall lock. */
  premiumLocked: boolean;
  /** The canonical access state (backend/app/domain.py's resolve_practice_access), computed
   * identically here, on the audio endpoint, and on session completion, so all three routes
   * agree on what's actually playable. */
  access: PracticeAccess;
  /** Content-review lifecycle (Work Package 2). Defaults are honest, not aspirational: "draft"
   * unless a real, named, qualified reviewer's review is recorded -- never inferred from a
   * practice simply being live. See docs/IMPLEMENTATION_LEDGER.md's Work Package 2 section. */
  contentVersion: number;
  circle: number;
  publicationState: 'draft' | 'published' | 'unpublished';
  reviewerStatus: 'draft' | 'reviewed';
  reviewerName: string | null;
  sourceRefs: string[];
  outcome: string | null;
  guidanceRequired: boolean;
}

export type PracticeAccess = 'available' | 'premium_required' | 'prerequisite_required' | 'unpublished';

export interface PracticesResponse {
  practices: Practice[];
}

/** A named practice-lineage within a tradition (e.g. Christian "Prayer"), tagged with exactly
 * one PracticeType. Not every tradition has pillar rows yet -- see Practice.pillarId. */
export interface Pillar {
  pillarId: string;
  tradition: Tradition;
  practiceType: PracticeType;
  title: string;
  description: string;
  order: number;
}

export interface PillarsResponse {
  pillars: Pillar[];
}

/** A religious observance/festival on a given date. `movable` flags a lunar/solar-shifting date
 * (Ramadan, Easter, Diwali, etc.) that needs annual re-seeding, vs. a fixed one (Christmas). */
export interface CalendarEvent {
  eventId: string;
  tradition: Tradition;
  title: string;
  description: string;
  /** ISO date, e.g. "2026-12-25". */
  date: string;
  movable: boolean;
  /** Work Package 3's calendar richness -- null means "not yet cataloged", never fabricated. */
  region: string | null;
  community: string | null;
  startTime: string | null;
  uncertainty: 'confirmed' | 'estimated';
  /** Links to this tradition's flagship pillar, when it has one -- lets the UI offer a direct
   * route to an eligible practice for this observance. */
  relatedPillarId: string | null;
}

export interface CalendarResponse {
  events: CalendarEvent[];
}

export interface PracticeAudio {
  practiceId: string;
  url: string;
  expiresAt: string;
  cached: boolean;
  /** The language asked for (?lang=, else the caller's stored locale, else "en"). */
  requestedLocale: Locale;
  /** The language actually narrated. Differs from requestedLocale only when `fallback` is true --
   * show this rather than assuming narration matches the UI language (review finding 1). */
  audioLocale: Locale;
  fallback: boolean;
  fallbackReason: NarrationFallbackReason | null;
  voiceId: string;
  contentVersion: number;
}

export type BookFormat = 'pdf' | 'epub' | 'txt';

export interface Book {
  bookId: string;
  title: string;
  tradition: Tradition;
  format: BookFormat;
  license: string;
  /** The edition's own language (e.g. a real Gutenberg Spanish Bible is "es"); "en" otherwise. */
  language?: string;
  /** Where the edition comes from (e.g. "Project Gutenberg" + its ebook page), or null. */
  sourceName?: string | null;
  sourceUrl?: string | null;
}

export interface LibraryResponse {
  books: Book[];
}

export interface LibraryDownload {
  bookId: string;
  url: string;
  expiresAt: string;
}

export interface PostSessionBody {
  practiceId: string;
  /** Honest elapsed seconds for a timed (guided/silent) session; omit for self_reported. An old
   * client sending only {practiceId, durationSeconds} is still accepted (mode "unspecified"). */
  durationSeconds?: number;
  /** Client-generated and stable across retries of the same completion (8-128 chars of
   * [A-Za-z0-9._:-]) -- a retry returns the stored result instead of double-counting. */
  idempotencyKey?: string;
  mode?: SessionMode;
  /** ISO-8601 with offset. */
  startedAt?: string;
  completedAt?: string;
  contentVersion?: number;
  /** Recorded for context only; the streak day is decided server-side. */
  timezone?: string;
  localDate?: string;
}

export interface PostSessionResponse {
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string;
  completedPracticeIds: string[];
  shieldsAvailable: number;
  shieldUsed: boolean;
  pilgrimRank: PilgrimRank;
  totalPracticeDays: number;
  practiceMilestone: PracticeMilestone;
  /** The recorded session occurrence (review fix F15). `replayed` is true when this is the stored
   * result of an already-recorded idempotencyKey. */
  session: SessionSummary;
}

export interface ReinforcementResponse {
  shieldsAvailable: number;
  claimed: boolean;
  /** True when the claim was recorded but Shields were already at the cap of 2. */
  atCap: boolean;
  /** The completed occurrence of today's assigned reinforcement that backed this claim. */
  sessionId: string;
  assignment: ReinforcementAssignment | null;
}

export interface LeaderboardEntry {
  displayName: string;
  currentStreak: number;
  rank: number;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
}

// --- Billing (Work Package 5, Whop) --------------------------------------------------------

/** POST /api/billing/checkout's response -- a URL to redirect the browser to. `tier` only ever
 * actually changes later, via a server-verified Whop webhook, never from this response or the
 * checkout return redirect -- see backend/app/handlers/billing.py. */
export interface CreateCheckoutResponse {
  checkoutUrl: string;
}

/** One Whop membership record behind the caller's paid entitlement (GET /api/billing/status). */
export interface BillingMembership {
  membershipId: string;
  status: string | null;
  entitled: boolean;
  cancelAtPeriodEnd: boolean;
  renewalPeriodEnd: string | null;
  paymentFailed: boolean;
  lastEventAt: string | null;
  /** Only ever a URL Whop itself sent for this membership; null means "manage it in your Whop
   * account" -- the app never constructs a Whop URL. */
  manageUrl: string | null;
}

/** GET /api/billing/status (code-review fix F3). `tier` is derived server-side:
 * any entitled paid membership OR an admin grant -- the two sources never overwrite each other. */
export interface BillingStatus {
  tier: Tier;
  paidPremium: boolean;
  adminPremiumGrant: boolean;
  /** Premium from before entitlement sources were recorded -- origin unknown, shown neutrally. */
  legacyPremium: boolean;
  billingConfigured: boolean;
  memberships: BillingMembership[];
}

export interface ApiError {
  error: string;
}

/** Structural shape shared by client.ts's real `ApiError` and mock.ts's `MockApiError` -- lets UI
 * code branch on a denied practice-access reason (see `PracticeAccess`) the same way regardless
 * of which one actually threw, without either module importing the other's class (client.ts
 * already imports mock.ts, so the reverse would be circular). */
export interface AccessDeniedError {
  status: number;
  access?: PracticeAccess;
  message: string;
}

export function isAccessDeniedError(e: unknown): e is AccessDeniedError {
  return (
    !!e &&
    typeof e === 'object' &&
    'status' in e &&
    typeof (e as { status: unknown }).status === 'number'
  );
}

// --- Review fixes (2026-09-10): Today summary, account deletion ------------------------------

/** GET /api/today's `practice` -- a preview (never the script/transcript), with the same access
 * state GET /api/practices reports and the language the content will actually be delivered in. */
export interface TodayPracticeSummary {
  practiceId: string;
  title: string;
  type: PracticeType;
  tradition: Tradition;
  pillarId: string | null;
  level: number | null;
  durationSeconds: number;
  description: string;
  access: PracticeAccess;
  language: string;
  /** True when the requested language's translation is missing and `language` is the fallback. */
  languageFallback: boolean;
}

/** DELETE /api/me. `identityDeleted` is false when the sign-in account couldn't be removed (the
 * server then answers 502 so the client can retry; the data itself is already gone). */
export interface DeleteMeResponse {
  deleted: boolean;
  journalEntriesDeleted: number;
  identityDeleted: boolean;
}

// --- Session occurrences + reinforcements (review fix F15) ------------------------------------

/** How a completion happened, as reported to POST /api/sessions: `guided` (timed, narration
 * actually played), `silent` (timed, no narration), `self_reported` (done away from the timer --
 * saved honestly with no duration). */
export type SessionMode = 'guided' | 'silent' | 'self_reported';

/** What the server recorded -- `unspecified` for an old client that sent no `mode`. */
export type RecordedSessionMode = SessionMode | 'unspecified';

export interface SessionSummary {
  sessionId: string;
  practiceId: string;
  mode: RecordedSessionMode;
  durationSeconds: number | null;
  localDate: string;
  contentVersion: number;
  replayed: boolean;
}

/** Why narration isn't in the requested language: no translation of this script yet, or no
 * suitable voice for that language. English narration is played in both cases. */
export type NarrationFallbackReason = 'translation_missing' | 'voice_unavailable';

export interface ReinforcementAssignment {
  practiceId: string;
  title: string;
}

/** GET /api/reinforcements -- today's assigned reinforcement (a repeat of an already-completed
 * practice, derived per user per local day) and whether a completion of it can be claimed. */
export interface ReinforcementStatus {
  localDate: string;
  assignment: ReinforcementAssignment | null;
  completedSessionId: string | null;
  claimedToday: boolean;
  shieldsAvailable: number;
  shieldCap: number;
}

export interface ClaimReinforcementBody {
  sessionId?: string;
}
